import * as z from "zod";
import { userFacingError } from "../authorization";
import { mutator } from "./mutator";

export const registerOAuthAppSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  redirectUris: z.array(z.string().min(1).max(2048)).min(1),
});

function isValidRedirectUri(uri: string): boolean {
  let u: URL;
  try {
    u = new URL(uri);
  } catch {
    return false;
  }
  if (u.hash) return false;
  // https anywhere; loopback http and custom native schemes are also allowed by
  // the authorization server (RFC 8252), but admins registering a server-to-
  // server app should use https, so the registry UI only accepts https.
  return u.protocol === "https:";
}

// Registering an OAuth app is the tenant-level trust decision (tier 1): it
// declares a client that may run the consent flow and obtain credentials. A
// service-account manager then authorizes the app per account (tier 2,
// authorizeOAuthAppForServiceAccount). Gated on tenant:oauth_apps_manage (held
// by tenant admins via allow_all). The app grants no access by itself.
export const registerOAuthApp = mutator({
  schema: registerOAuthAppSchema,
  resolve: {},
  permissions: [
    {
      permission: "tenant:oauth_apps_manage",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({ tx, ctx, args: { id, name, redirectUris } }) => {
    if (!redirectUris.every(isValidRedirectUri)) {
      throw userFacingError("Redirect URIs must be HTTPS URLs");
    }
    const now = Date.now();
    await tx.mutate.oauthClient.insert({
      id,
      name,
      redirectUris,
      grantTypes: ["authorization_code", "refresh_token"],
      // The general-API resource is service-account-bound and does not use
      // OAuth scopes for authority (the SA's grants are the authority); an empty
      // scope set keeps the registration explicit.
      scopes: [],
      // Admin-registered apps target the general API, so the authorize endpoint
      // defaults `resource` to the api resource when a generic OAuth2 client
      // (which can't send a `resource` param) omits it.
      resourceKind: "api",
      tokenEndpointAuthMethod: "none",
      disabled: false,
      createdAt: now,
      updatedAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
